import { WidgetMessageType } from '@/types/enums/WidgetMessageType';
import { WidgetStorageId } from '@/types/enums/WidgetStorageId';
import {
  CustomerInfoType,
  TravelerType,
  WidgetConfig
} from '@/types/widgetConfig';
import { checkIfIFrame } from '@/utils/checkIfIFrame';
import { onMsg } from '@/utils/postMessage';
import { QueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import storage from 'local-storage-fallback';
import { useEffect, useMemo, useState } from 'react';

import useTrackEvent from '../analytics/useTrackEvent';
import useWidgetCurrency from '../state/WidgetCurrency/useWidgetCurrency';
import useWidgetLanguage from '../state/WidgetLanguage/useWidgetLanguage';
import { customerInfoAtom } from '../state/customerInfo';
import useWidgetHostConfiguration from './useWidgetHostConfiguration';

function parseCustomerInfo(customerInfoString: string): CustomerInfoType | {} {
  try {
    return JSON.parse(customerInfoString);
  } catch (e) {
    console.debug({ parseError: e });
    return {};
  }
}

const useTravelOneWidget = (queryClient: QueryClient) => {
  const isIframe = useMemo(checkIfIFrame, []);
  const [trustedOrigin, setTrustedOrigin] = useState('');
  const [messageOrigin, setMessageOrigin] = useState('');
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig>();
  const [showModal, setShowModal] = useState(false);
  const { trackWidgetInitEvent } = useTrackEvent();
  const { hostDetails } = useWidgetHostConfiguration(queryClient, widgetConfig);
  const [_, setCustomerInfoState] = useAtom(customerInfoAtom);
  const [isLoadingModalState, setIsLoadingModalState] = useState(true);
  const { initWidgetLanguage } = useWidgetLanguage();
  const { initWidgetCurrency } = useWidgetCurrency(queryClient);

  useEffect(() => {
    onMsg(WidgetMessageType.Init, '*');
  }, []);

  useEffect(() => {
    if (isIframe) {
      window.addEventListener('message', async e => {
        // to check if the message initiator has rights to communicate with the widget
        if (typeof e.data !== 'object' && e.data !== null) {
          let data;
          try {
            data = JSON.parse(e.data);
          } catch (err) {
            console.log(err.message);
          }
          if (data?.message === WidgetMessageType.WidgetConfig) {
            const config = data.config as WidgetConfig;
            storage.setItem(WidgetStorageId.HostId, config.hostId);
            setWidgetConfig(config);

            await initWidgetCurrency(config.currency);
            initWidgetLanguage(config.language);

            const customerInfoObject = parseCustomerInfo(config.customerInfo);
            fixCustomerInfoObject(customerInfoObject);
            setCustomerInfoState({
              customerInfo: customerInfoObject,
              isInitialized: true
            });

            trackWidgetInitEvent();

            setMessageOrigin(e.origin);
          } else if (data?.message === WidgetMessageType.StartMain) {
          }
        }
      });
    } else {
      const customerInfoFromParams = Object.fromEntries(
        new URLSearchParams(window.location.search)
      );

      if (customerInfoFromParams.hostId) {
        window.TravelOneConfig.hostId = customerInfoFromParams.hostId;
      }

      if (customerInfoFromParams.currency) {
        window.TravelOneConfig.currency = customerInfoFromParams.currency;
      }

      if (customerInfoFromParams.language) {
        window.TravelOneConfig.language = customerInfoFromParams.language;
      }

      console.log(window.TravelOneConfig);
      setWidgetConfig(window.TravelOneConfig);
      storage.setItem(WidgetStorageId.HostId, window.TravelOneConfig.hostId);
      initWidgetCurrency(window.TravelOneConfig.currency).catch(ex => {
        //
      });
      initWidgetLanguage(window.TravelOneConfig.language);

      fixCustomerInfoObject(customerInfoFromParams);
      setCustomerInfoState({
        customerInfo: customerInfoFromParams,
        isInitialized: true
      });
      trackWidgetInitEvent();
    }
  }, []);

  useEffect(() => {
    if (hostDetails) {
      if (isIframe) {
        const trustedOrigins = ['https://js.stripe.com'];

        const hostUrl = '*';

        if (hostUrl !== '') {
          trustedOrigins.push(hostUrl);
        }

        if (true || trustedOrigins.includes(messageOrigin)) {
          setTrustedOrigin(hostUrl);
          window.TravelOneConfig = {
            ...window.TravelOneConfig,
            ...widgetConfig
          };
          setShowModal(true);
        } else {
          setShowModal(false);
          onMsg(WidgetMessageType.HideIframe, messageOrigin);
        }
      } else {
        setShowModal(true);
      }
      setIsLoadingModalState(false);
    }
  }, [hostDetails, messageOrigin, widgetConfig]);

  return { showModal, isLoadingModalState };
};

export default useTravelOneWidget;

function fixCustomerInfoObject(customerInfoObject: CustomerInfoType) {
  if (
    customerInfoObject.destination &&
    customerInfoObject.eventDate &&
    !customerInfoObject.travelers
  ) {
    customerInfoObject.travelers = [TravelerType.Adult];
    customerInfoObject.adults = 1;
    customerInfoObject.rooms = 1;
  }
}
